import type { FieldsAttachmentModel } from "@overmap-ai/common"
import { type Field, FieldSchema, type FieldValues, type ValueOfField } from "@overmap-ai/forms"

import { createFilterDefConfig } from "../../schemas"
import { BooleanFieldFilterDef } from "../BooleanFieldFilterDef"
import { CheckboxListFieldFilterDef } from "../CheckboxListFieldFilterDef"
import { DateFieldFilterDef } from "../DateFieldFilterDef"
import { MultiSelectFieldFilterDef } from "../MultiSelectFieldFilterDef"
import { MultiStringFieldFilterDef } from "../MultiStringFieldFilterDef"
import { NumberFieldFilterDef } from "../NumberFieldFilterDef"
import { OTPFieldFilterDef } from "../OTPFieldFilterDef"
import { PassFailFieldFilterDef } from "../PassFailFieldFilterDef"
import { RadioFieldFilterDef } from "../RadioFieldFilterDef"
import { RatingFieldFilterDef } from "../RatingFieldFilterDef"
import { ScanFieldFilterDef } from "../ScanFieldFilterDef"
import { SelectFieldFilterDef } from "../SelectFieldFilterDef"
import { StringFieldFilterDef } from "../StringFieldFilterDef"
import { TextFieldFilterDef } from "../TextFieldFilterDef"
import { TimeFieldFilterDef } from "../TimeFieldFilterDef"
import { UploadFieldFilterDef } from "../UploadFieldFilterDef"
import { URLFieldFilterDef } from "../URLFieldFilterDef"

export const getFilterDefOfField = (field: Field) => {
	switch (field.type) {
		case "string":
			return new StringFieldFilterDef(field)
		case "text":
			return new TextFieldFilterDef(field)
		case "boolean":
			return new BooleanFieldFilterDef(field)
		case "number":
			return new NumberFieldFilterDef(field)
		case "date":
			return new DateFieldFilterDef(field)
		case "select":
			return new SelectFieldFilterDef(field)
		case "multi-string":
			return new MultiStringFieldFilterDef(field)
		case "multi-select":
			return new MultiSelectFieldFilterDef(field)
		case "qr":
			return new ScanFieldFilterDef(field)
		case "radio":
			return new RadioFieldFilterDef(field)
		case "checkbox-list":
			return new CheckboxListFieldFilterDef(field)
		case "otp":
			return new OTPFieldFilterDef(field)
		case "pass-fail":
			return new PassFailFieldFilterDef(field)
		case "rating":
			return new RatingFieldFilterDef(field)
		case "url":
			return new URLFieldFilterDef(field)
		case "time":
			return new TimeFieldFilterDef(field)
		case "upload":
			return new UploadFieldFilterDef(field)
	}
}

export const getFilterDefConfigOfField = <TData>(
	field: Field,
	getValues: (data: TData) => [FieldValues, FieldsAttachmentModel[]],
) => {
	return createFilterDefConfig({
		filterDef: getFilterDefOfField(field),
		accessorFn: (data: TData) => {
			const [values, attachmnets] = getValues(data)
			const value = values[field.id] || null

			switch (field.type) {
				case "pass-fail":
					if (!field.isValue(value)) return null
					return value.status
				case "url":
					if (!field.isValue(value)) return null
					return value?.toString() ?? null
				case "upload":
					return attachmnets.filter(({ field_identifier }) => field_identifier === field.id).length
				default:
					return value as ValueOfField<typeof field> | null
			}
		},
	})
}

export const getFilterDefConfigsForFields = <TData>(
	fields: Field[],
	getValues: (data: TData) => [FieldValues, FieldsAttachmentModel[]],
) => {
	return fields.map((field) => getFilterDefConfigOfField(field, getValues))
}

export const getFilterDefConfigsForFieldSchema = <TData>(
	fieldSchema: FieldSchema,
	getValues: (data: TData) => [FieldValues, FieldsAttachmentModel[]],
) => {
	return getFilterDefConfigsForFields(fieldSchema.getFields(), getValues)
}
